import { Injectable, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { CookiesService } from './cookies.service';
import { InitializerService } from '../../initializer/initializer.service';
import { IResponse } from '../../http/interfaces/response.interface';

export interface ISeoPageDataResult {
    SeoPageDataList: ISeoPageDataModel[];
}

export interface IFAQ {
    QuestionText: string;
    AnswerText: string;
}

export interface ILcImage {
    Url: string;
    Alt: string;
}

export interface ISeoPageDataModel {
    PageId: string;
    Title: string;
    Description: string;
    Keywords: string;
    QuestionsAndAnswers: IFAQ[];
    Images: ILcImage[];
}

@Injectable({
    providedIn: 'root'
})
export class SeoService {

    constructor(
        private router: Router,
        private httpSvc: HttpClient,
        private initializer: InitializerService,
        private metasrv: Meta,
        private titlesrv: Title,
        private route: ActivatedRoute,
        private serverService: CookiesService,
        @Inject(DOCUMENT) private readonly document: any) {

    }

    start(): void {
        this.serverService.server8Mode$.subscribe(v => {
            let title = this.titlesrv.getTitle();
            if (v) {
                if (!title.includes('שדרה 8')) {
                    this.titlesrv.setTitle('שדרה 8 ' + title);
                }
            }
            else {
                if (title.includes('שדרה 8')) {
                    this.titlesrv.setTitle(title.replace('שדרה 8', '').trim());
                }
            }
        });

        let apiUrl = this.initializer.getAppConfigs().apiUrl;

        this.router.events.pipe(filter(e => e instanceof NavigationEnd && ['/cards/card', '/benefits/lobby'].every(str => !e.urlAfterRedirects.toLowerCase().startsWith(str))),
            map((e) => ({e: e, route: this.route.snapshot})),
            map((obj) => {
                while (obj.route.firstChild) {
                    obj.route = obj.route.firstChild;
                }
                return ({e: obj.e, route: obj.route});
            }),
            filter((obj) => !obj.route.data || !obj.route.data['skipSeoPageData']),
            map((obj) => (obj.e as NavigationEnd).urlAfterRedirects.split('?')[0])).subscribe(path => {
                this.httpSvc.get<IResponse<ISeoPageDataModel>>(`${apiUrl}/seopagedata/getPage?routePageId=${path}`).subscribe(seo => {if (seo.Result) this.setSeoPageData(seo.Result, path)});
            });
    }

    setQuestionAndAnswers(questionsAndAnswers: IFAQ[]) {
        if (!questionsAndAnswers || this.document.querySelector(`script[id="faqScript"]`)) {
            return;
        }
        let data = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": []
        };
        data['mainEntity'] = questionsAndAnswers.map(qa => this.buildItemForFaq(qa.QuestionText, qa.AnswerText));
        let node = this.document.createElement('script');
        node.type = 'application/ld+json';
        node.async = true;
        node.charset = 'utf-8';
        node.innerHTML = JSON.stringify(data);
        node.id = 'faqScript';
        this.document.getElementsByTagName('head')[0].appendChild(node);
    }

    private buildItemForFaq(q, a) {
        let item = {
            "@type": "Question",
            "name": null ,
            "acceptedAnswer": {
              "@type": "Answer",
              "text":null }
            };
        item['name'] = q;
        item['acceptedAnswer']['text'] = a;
        return item;
    }

    setImages(images: ILcImage[]) {
        if (!images || this.document.querySelector(`script[id="imagesScript"]`)) {
            return;
        }
        let data = {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "image": []
        };
        data['image'] = images.map(i => i.Url);
        let node = this.document.createElement('script');
        node.type = 'application/ld+json';
        node.async = true;
        node.charset = 'utf-8';
        node.innerHTML = JSON.stringify(data);
        node.id = 'imagesScript';
        this.document.getElementsByTagName('head')[0].appendChild(node);
    }

    private setSeoPagesData(res: ISeoPageDataResult, routePageId: string) {
        let result = this.getResultByPageId(res, routePageId);
        this.setSeoPageData(result, routePageId);
    }

    private setSeoPageData(seo: ISeoPageDataModel, routePageId: string) {
        if (seo) {
            this.setTitle(seo.Title);
            this.setDescription(seo.Description);
            this.setKeywords(seo.Keywords);
            this.setQuestionAndAnswers(seo.QuestionsAndAnswers);
            this.setImages(seo.Images);
        } else {
            // this.logger.warning(`seo data not found for path: ${routePageId}`);
        }
    }

    // recursivly search through the path hierarchy to find the most specific definition in the list
    private getResultByPageId(res: ISeoPageDataResult, routePageId: string): ISeoPageDataModel {
        let result = res.SeoPageDataList.find(seo => seo.PageId.toLowerCase() === routePageId.toLowerCase());
        if (result) { return result; }
        let pathSplit = routePageId.split('/');
        let pageId = pathSplit.slice(0, pathSplit.length - 1).join('/');
        if (!pageId) {
            return res.SeoPageDataList.find(seo => seo.PageId.toLowerCase() === "/");
        }
        return this.getResultByPageId(res, pageId);
    }

    setTitle(title: string) {
        if (title) {
            this.titlesrv.setTitle(title);
            if (this.serverService.isCookieEnabled('eve')) {
                this.serverService.server8Mode$.next(true);
            }

        }
    }
    setDescription(description: string) {
        if (description) {
            this.metasrv.updateTag({ name: 'description', content: description });
            const desc = this.document.querySelector('head > meta[name=description]');
            this.hoistElement(desc);
        }
    }
    setKeywords(keywords) {
        if (keywords) {
            this.metasrv.updateTag({ name: 'keywords', content: keywords });
            const keywordsEle = this.document.querySelector('head > meta[name=keywords]');
            this.hoistElement(keywordsEle);
        }
    }

    private hoistElement(element: HTMLElement) {
        if (!element) {
            return;
        }
        this.document.head.insertBefore(element, this.document.head.firstChild);
    }
}
